/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, context provider, and custom hooks
 */

'use client'

import {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

const AccountTradersServiceContext = createContext(undefined)

const AccountTradersServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}) => {
  return (
    <ResourceApiServiceProvider
      Context={AccountTradersServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      valueCallback={(axios) => ({
        create: async (payload) => {
          const {data} = await axios.post(`/api/v1/org/account_traders`, payload)
          return data
        },
        list: async (query) => {
          const {data} = await axios.get(
            `/api/v1/org/account_traders${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        updateBatch: async (payload) => {
          const {data} = await axios.patch(`/api/v1/org/account_traders`, payload)
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

AccountTradersServiceProvider.propTypes = {
  children: PropTypes.any,
  baseApiHost: PropTypes.string.isRequired,
  handleUnauthorized: PropTypes.func
}

const useAccountTradersService = () => {
  const context = useContext(AccountTradersServiceContext)
  if (context === undefined) {
    throw new Error('AccountTradersServiceContext was not provided. Make sure your component is a child of the AccountTradersServiceProvider.')
  }
  return context
}

const useAccountTradersQuery = ({query, ...otherProps}) => {
  const accountTradersService = useAccountTradersService()
  return useSimpleQuery({
    queryKey: `account_traders-list-${JSON.stringify(query)}`,
    queryFn: async () => accountTradersService.list(query),
    ...otherProps
  })
}

export {AccountTradersServiceProvider, useAccountTradersService, useAccountTradersQuery}
